import {hideOverFooter} from '../../modules/hideOverFooter.js';

/**
 * Run countdowns (if any)
 */
const runCountdown = () => {
    const countdownClock = document.querySelector('.countdown');

    if (countdownClock) {
        import('../../modules/countdownClock.js').then(({CountdownClock}) => {
            const clock = countdownClock.querySelector('.clock');
            const endTimestamp = countdownClock.dataset.closeTimestamp;

            new CountdownClock(clock, endTimestamp, () => window.location.reload());
        });
    }
};

/**
 * Initialize hero header scroll behavior (opt-in via js-landingPageHeroHeader class)
 *
 * This behavior is activated when:
 * - Hero header has the `js-landingPageHeroHeader` class (opt-in)
 * - Nav header has the `js-minimalNavHeader` class
 * - Nav header starts with `hidden` class (passed via headerExtraClasses prop in app-minimal layout)
 *
 * Behavior:
 * - Initial state: Nav header is hidden (fixed, off-screen), hero header is visible
 * - When user scrolls past hero header: Hero header fades out, nav header slides down
 * - When user scrolls back to top: Hero header fades in, nav header slides up
 *
 * Implementation:
 * - Nav header uses `position: fixed` throughout to avoid any layout shifts
 * - Hero header stays in document flow and uses opacity for fade transitions
 * - An IntersectionObserver with a 70px top inset reports the crossing, so no scroll frame reads layout
 * - No layout shifts = no flickering
 */
const initHeroHeaderScrollBehavior = () => {
    const heroHeader = document.querySelector('.js-landingPageHeroHeader');
    const navHeader = document.querySelector('.js-minimalNavHeader');

    // Only activate if hero header has the opt-in class and nav header starts hidden
    if (!heroHeader || !navHeader || !navHeader.classList.contains('hidden')) {
        return;
    }

    // Setup nav header: use fixed positioning (never causes layout shifts)
    // Override the default sticky positioning with fixed
    navHeader.classList.remove('hidden');
    navHeader.classList.add('!fixed', 'opacity-0', '-translate-y-full', 'transition-[translate,opacity]', 'duration-250', 'ease-in-out');

    // Setup hero header for CSS transitions
    heroHeader.classList.add('transition-opacity', 'duration-250', 'ease-in-out');

    let isNavHeaderVisible = false;
    let showNavHeaderTimeout = null;

    const showNavHeader = () => {
        navHeader.classList.remove('opacity-0', '-translate-y-full');
        heroHeader.classList.add('opacity-0', 'pointer-events-none');
        isNavHeaderVisible = true;
        // The sticky bottom bar waits for this moment (see components/landing-page/section-stack.blade.php).
        window.dispatchEvent(new CustomEvent('landingPage:heroHeaderHidden'));
    };

    /** @param {boolean} shouldShowNavHeader Whether the hero header's bottom edge is within 70px of the viewport top, or above it. */
    const updateHeaders = (shouldShowNavHeader) => {
        if (shouldShowNavHeader && !isNavHeaderVisible && !showNavHeaderTimeout) {
            // Delay showing nav header by 250ms to avoid flicker on quick scrolls
            showNavHeaderTimeout = setTimeout(() => {
                showNavHeader();
                showNavHeaderTimeout = null;
            }, 250);
        } else if (!shouldShowNavHeader) {
            // Clear pending show timeout if user scrolls back up
            if (showNavHeaderTimeout) {
                clearTimeout(showNavHeaderTimeout);
                showNavHeaderTimeout = null;
            }
            if (isNavHeaderVisible) {
                // Hide nav header (slide up), show hero header (fade in)
                navHeader.classList.add('opacity-0', '-translate-y-full');
                heroHeader.classList.remove('opacity-0', 'pointer-events-none');
                isNavHeaderVisible = false;
            }
        }
    };

    // The observer's first report covers a page that loads scrolled. The 70px inset is the breathing room against flicker at the edge.
    new IntersectionObserver(([entry]) => updateHeaders(!entry.isIntersecting), {rootMargin: '-70px 0px 0px 0px'}).observe(heroHeader);
};

/**
 * Hide the sticky bottom bar when the "Learn Design From the Best" CTA
 * is visible, or when the site footer is. Prevents two competing CTAs from overlapping at the bottom.
 */
function initStickyBarVisibility() {
    const ctaSection = document.querySelector('.js-readyToDesignALifeCta');
    const stickyBar = document.querySelector('.js-landingPageBottomBar');
    const siteFooter = document.querySelector('.js-siteFooter');

    if (stickyBar instanceof HTMLElement && siteFooter instanceof HTMLElement) {
        hideOverFooter(stickyBar, siteFooter);
    }

    if (!ctaSection || !stickyBar) {
        return;
    }

    const observer = new IntersectionObserver(
        ([entry]) => {
            if (entry.isIntersecting) {
                stickyBar.classList.add('translate-y-full', 'pointer-events-none');
            } else {
                stickyBar.classList.remove('translate-y-full', 'pointer-events-none');
            }
        },
        {threshold: 0}
    );

    observer.observe(ctaSection);
}

/** Initialize the page */
function initPage() {
    document.addEventListener('htmx:afterSwap', runCountdown);
    initHeroHeaderScrollBehavior();
    initStickyBarVisibility();
}

document.readyState === 'loading' ? document.addEventListener('DOMContentLoaded', initPage) : initPage();
